import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse, PagedResponse } from '../models/api-response';
import { AppError, isAppError } from '../errors/app-error';
import { SUPPRESS_AUTH_REDIRECT_HEADER, SUPPRESS_TOAST_HEADER } from '../interceptors/http-context';
import { LanguageService } from './language.service';
import { httpErrorMessage, resolveServerFailureToastMessage } from '../i18n/http-error-messages';

export interface ApiRequestOptions {
  /** Suppress the global toast notification on error. Use when the component shows the error inline. */
  suppressToast?: boolean;
  /** Suppress the automatic 401 → /login redirect. Used by the login endpoint itself. */
  suppressAuthRedirect?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private baseUrl = environment.apiUrl;
  /** Not TranslateService — it injects HttpClient and would circularly depend on this pipeline. */
  private language = inject(LanguageService);

  constructor(private http: HttpClient) {}

  private buildHeaders(opts?: ApiRequestOptions): HttpHeaders {
    const lang = this.language.currentLang === 'ar' ? 'ar' : 'en';
    let headers = new HttpHeaders().set('Accept-Language', lang);
    if (opts?.suppressToast) headers = headers.set(SUPPRESS_TOAST_HEADER, '1');
    if (opts?.suppressAuthRedirect) headers = headers.set(SUPPRESS_AUTH_REDIRECT_HEADER, '1');
    return headers;
  }

  private withParams(path: string, params?: Record<string, unknown>): { url: string; httpParams: HttpParams } {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(k => {
        if (params[k] != null && params[k] !== '') httpParams = httpParams.set(k, String(params[k]));
      });
    }
    return { url: `${this.baseUrl}/${path}`, httpParams };
  }

  /** Format an `ApiResponse` failure body into a single human-readable string. */
  private formatApiFailureMessage(body: ApiResponse<unknown>): string {
    if (Array.isArray(body.errors) && body.errors.length) {
      return body.errors.join(' — ');
    }
    return (body.message ?? '').trim();
  }

  /**
   * Normalize anything thrown by the HTTP pipeline into an `AppError`.
   * The error interceptor already converts `HttpErrorResponse` → `AppError`, so
   * here we only need to wrap the in-pipe `success: false` ApiResponse case.
   */
  private toAppError(err: unknown): AppError {
    if (isAppError(err)) return err;
    if (err instanceof AppError) return err;
    const message =
      (err instanceof Error && err.message) ||
      (typeof err === 'string' && err) ||
      httpErrorMessage('errors.unknown', this.language.currentLang);
    return new AppError('unknown', 0, 'errors.unknown', message);
  }

  /** Throw an AppError representing an `ApiResponse` body whose `success` flag is false. */
  private apiFailureToAppError(body: ApiResponse<unknown>): AppError {
    const serverMessage = this.formatApiFailureMessage(body);
    const userMessage = resolveServerFailureToastMessage(serverMessage || undefined, this.language.currentLang);
    return new AppError('validation', 200, 'errors.validation', userMessage, serverMessage);
  }

  /** Raw GET - returns full response (e.g. for ApiResponse wrapper) */
  get<T>(path: string, params?: Record<string, unknown>, opts?: ApiRequestOptions): Observable<T> {
    const { url, httpParams } = this.withParams(path, params);
    return this.http.get<T>(url, { params: httpParams, headers: this.buildHeaders(opts) });
  }

  /** GET and unwrap ApiResponse<T> to T */
  getData<T>(path: string, params?: Record<string, unknown>, opts?: ApiRequestOptions): Observable<T> {
    return this.get<ApiResponse<T>>(path, params, opts).pipe(
      map(res => {
        if (!res.success || res.data === undefined) {
          throw this.apiFailureToAppError(res as ApiResponse<unknown>);
        }
        return res.data;
      }),
      catchError(err => throwError(() => this.toAppError(err)))
    );
  }

  /** GET and unwrap ApiResponse<PagedResponse<T>> to PagedResponse<T> */
  getPaged<T>(path: string, params?: Record<string, unknown>, opts?: ApiRequestOptions): Observable<PagedResponse<T>> {
    return this.getData<PagedResponse<T>>(path, params, opts);
  }

  post<T>(path: string, body: unknown, opts?: ApiRequestOptions): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}/${path}`, body, { headers: this.buildHeaders(opts) });
  }

  /** POST and unwrap ApiResponse<T> to T */
  postData<T>(path: string, body: unknown, opts?: ApiRequestOptions): Observable<T> {
    return this.http.post<ApiResponse<T>>(`${this.baseUrl}/${path}`, body, { headers: this.buildHeaders(opts) }).pipe(
      map(res => {
        if (!res.success) {
          throw this.apiFailureToAppError(res as ApiResponse<unknown>);
        }
        return (res.data ?? undefined) as T;
      }),
      catchError(err => throwError(() => this.toAppError(err)))
    );
  }

  put<T>(path: string, body: unknown, opts?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}/${path}`, body, { headers: this.buildHeaders(opts) });
  }

  putData<T>(path: string, body: unknown, opts?: ApiRequestOptions): Observable<T> {
    return this.http.put<ApiResponse<T>>(`${this.baseUrl}/${path}`, body, { headers: this.buildHeaders(opts) }).pipe(
      map(res => {
        if (!res.success) {
          throw this.apiFailureToAppError(res as ApiResponse<unknown>);
        }
        return (res.data ?? undefined) as T;
      }),
      catchError(err => throwError(() => this.toAppError(err)))
    );
  }

  delete<T>(path: string, opts?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}/${path}`, { headers: this.buildHeaders(opts) });
  }

  uploadFile(path: string, file: File, opts?: ApiRequestOptions): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<{ url: string }>>(`${this.baseUrl}/${path}`, formData, { headers: this.buildHeaders(opts) }).pipe(
      map(res => {
        if (!res.success) throw this.apiFailureToAppError(res as ApiResponse<unknown>);
        if (!res.data?.url) {
          throw new AppError(
            'validation',
            200,
            'errors.validation',
            httpErrorMessage('errors.unknown', this.language.currentLang),
          );
        }
        return res.data;
      }),
      catchError(err => throwError(() => this.toAppError(err)))
    );
  }
}
